import { useApolloClient, useSubscription } from '@apollo/client';

import { ActivitiesContentType } from 'global/constants';
import { STUDENT_ACTIVITY_ANSWERS_SUBSCRIPTION } from '../../present/activity-answer-subscription.graphql';
import type {
  StudentsActivityAnswersSubscription,
  StudentsActivityAnswersSubscriptionVariables,
} from '../../present/activity-answer-subscription.graphql.generated';
import { updateStudentAnswersInCache } from '../logic/update-student-answers-in-cache';
import { updateStudentAnswersSummaryInCache } from '../logic/update-student-answers-summary-in-cache';
import { updateClassAnswersInCache } from 'approot/activity-builder/classroom/logic/update-class-answers-in-cache';

type Props = {
  contentId: number;
  contentType: ActivitiesContentType;
  contentVersion: string;
  userClassId?: number | null;
  skip: boolean;
};

export const useSubcribeToStudentAnswerUpdates = ({
  contentId,
  contentType,
  contentVersion,
  userClassId,
  skip,
}: Props) => {
  const client = useApolloClient();

  useSubscription<
    StudentsActivityAnswersSubscription,
    StudentsActivityAnswersSubscriptionVariables
  >(STUDENT_ACTIVITY_ANSWERS_SUBSCRIPTION, {
    skip: !userClassId || skip,
    variables: {
      input: {
        contentId: `${contentId}`,
        contentType,
        classId: userClassId!,
        contentVersion,
      },
    },
    onData: pushedUpdate => {
      const updatedAnswer =
        pushedUpdate.data.data?.studentsActivityAnswersSubscription;
      if (updatedAnswer && userClassId) {
        // handle update where answers are fetched for a component
        updateStudentAnswersInCache({
          client,
          updatedAnswer,
          contentId,
          contentType,
          contentVersion,
          userClassId,
          loadDrawingData: false,
        });

        if (updatedAnswer.studentId) {
          // handle update where answers are fetched by studentId
          updateStudentAnswersInCache({
            client,
            updatedAnswer,
            contentId,
            contentType,
            contentVersion,
            userClassId,
            studentId: updatedAnswer.studentId,
            loadDrawingData: false,
          });
        }

        // handle update for class answers
        updateClassAnswersInCache({
          client,
          updatedAnswer,
          contentId,
          contentType,
          contentVersion,
          userClassId,
        });

        // handle update for summary queries
        updateStudentAnswersSummaryInCache({
          client,
          updatedAnswer,
          contentId,
          contentType,
          contentVersion,
          userClassId,
        });
      }
    },
  });
};
